import { FormEvent, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import { Card, CardContent, Container, IconButton, LinearProgress, Paper, Stack } from '@mui/material'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlertWithTimeout } from '../../redux/features/alerts/alertsHelpers'
import { addAlert } from '../../redux/features/alerts/alertsSlice'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getAuthService } from '../../services/authService'
import { getErrorModel } from '../../utils/getErrorModel'

interface PasswordStrength {
    score: number
    feedback: string
    color: 'error' | 'warning' | 'success'
}

interface PasswordRequirement {
    label: string
    test: (password: string) => boolean
    met: boolean
}

export const ConfirmAccount = () => {
    const dispatch = useAppDispatch()
    const teams = useAppSelector(state => state.teams.teams)

    const [username, setUsername] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [usernameError, setUsernameError] = useState<string>('')
    const [passwordError, setPasswordError] = useState<string>('')
    const [confirmError, setConfirmError] = useState<string>('')
    const [favoriteTeamError, setFavoriteTeamError] = useState<string>('')
    const [favoriteTeamID, setFavoriteTeamID] = useState<number>(0)
    const [processing, setProcessing] = useState<boolean>(false)
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
        score: 0,
        feedback: '',
        color: 'error'
    })
    const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
        { label: 'At least 8 characters', test: (pwd) => pwd.length >= 8, met: false },
        { label: 'Contains at least 1 number', test: (pwd) => /[0-9]/.test(pwd), met: false },
        { label: 'Contains at least 1 special character', test: (pwd) => /[^a-zA-Z0-9]/.test(pwd), met: false },
        { label: 'Contains at least 1 uppercase letter', test: (pwd) => /[A-Z]/.test(pwd), met: false },
        { label: 'Contains at least 1 lowercase letter', test: (pwd) => /[a-z]/.test(pwd), met: false }
    ])

    const url = new URL(window.location.href)
    const searchParams = url.searchParams
    const token = searchParams.get('token')

    const fieldBlank = !username || !password || !confirmPassword || !favoriteTeamID
    const hasError = !!usernameError || !!passwordError || !!confirmError || !!favoriteTeamError
    const unmetRequirements = passwordRequirements.filter(req => !req.met).length
    const submitDisabled = processing || fieldBlank || hasError || unmetRequirements > 0

    const validatePasswordRequirements = (pwd: string) => {
        const updatedRequirements = passwordRequirements.map(req => ({
            ...req,
            met: req.test(pwd)
        }))
        setPasswordRequirements(updatedRequirements)
        return updatedRequirements
    }

    const validatePasswordStrength = (pwd: string): PasswordStrength => {
        if (!pwd) {
            return { score: 0, feedback: '', color: 'error' }
        }

        const requirements = validatePasswordRequirements(pwd)
        const metRequirements = requirements.filter(req => req.met).length
        const score = metRequirements

        let feedback = ''
        let color: 'error' | 'warning' | 'success' = 'error'

        if (score === 0) {
            feedback = 'Very weak'
            color = 'error'
        } else if (score <= 2) {
            feedback = 'Weak'
            color = 'error'
        } else if (score === 3 || score === 4) {
            feedback = 'Good'
            color = 'warning'
        } else if (score === 5) {
            feedback = 'Strong'
            color = 'success'
        }

        return { score, feedback, color }
    }

    const handlePasswordChange = (value: string) => {
        setPassword(value)
        setPasswordError('')
        setPasswordStrength(validatePasswordStrength(value))

        // Re-validate confirm password if it exists
        if (confirmPassword) {
            handleConfirmPasswordValidation(confirmPassword, value)
        }
    }

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value)
        handleConfirmPasswordValidation(value, password)
    }

    const handleConfirmPasswordValidation = (confirm: string, pwd: string) => {
        if (!confirm) {
            setConfirmError('')
            return
        }

        if (confirm !== pwd) {
            setConfirmError('Passwords do not match')
        } else {
            setConfirmError('')
        }
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        confirmAccount()
    }

    const handleSubmitClick = () => {
        confirmAccount()
    }

    const confirmAccount = async () => {
        if (!username || !password || !confirmPassword || processing || !favoriteTeamID) {
            return
        }

        if (password !== confirmPassword) {
            setConfirmError('Passwords do not match')
        }

        const unmetRequirements = passwordRequirements.filter(req => !req.met)
        if (unmetRequirements.length > 0) {
            setPasswordError('Please meet all password requirements')
            return
        }

        setProcessing(true)
        try {
            const authService = await getAuthService()
            await authService.confirmAccount(username, password, favoriteTeamID, token ?? '')
                .then(() => {
                    console.log('Account confirmed!')
                })
        } catch (err) {
            const e = await getErrorModel(err)
            if (e?.errors?.length > 0) {
                const message = e.errors[0].message
                if (message === 'username already exists') {
                    const alert = new AppAlert('error', 'An account with that username already exists', true)
                    dispatch(addAlert(alert.toJSON()))
                    return
                }
            }
            const alert = new AppAlert('error', 'Something went wrong, try again later', true)
            addAlertWithTimeout(dispatch, alert)
        } finally {
            setProcessing(false)
        }
    }

    const handleUsernameFocus = () => {
        setUsernameError('')
    }

    const handlePasswordFocus = () => {
        setPasswordError('')
    }

    const handleConfirmPasswordFocus = () => {
        setConfirmError('')
    }

    const handleFavoriteTeamFocus = () => {
        setFavoriteTeamError('')
    }

    const handleUsernameBlur = () => {
        // Add validation if needed
    }

    const handlePasswordBlur = () => {
        if (!password) {
            setPasswordError('Password is required')
            return
        }

        const unmetRequirements = passwordRequirements.filter(req => !req.met)
        if (unmetRequirements.length > 0) {
            setPasswordError('Please meet all password requirements')
            return
        }

        setPasswordError('')
    }

    const handleConfirmPasswordBlur = () => {
        handleConfirmPasswordValidation(confirmPassword, password)
    }

    const handleFavoriteTeamBlur = () => {
        if (!favoriteTeamID) {
            setFavoriteTeamError('Favorite team is required')
        }
    }

    return (
        <Box sx={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4
        }}>
            <Container component='div' maxWidth='sm'>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <PersonAddOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                        Confirm Account
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Complete your account setup
                                    </Typography>
                                </Box>
                                <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            required
                                            fullWidth
                                            error={!!usernameError}
                                            helperText={usernameError}
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            onFocus={handleUsernameFocus}
                                            onBlur={handleUsernameBlur}
                                            label='Username'
                                            autoComplete='username'
                                            autoFocus
                                        />
                                        <Box>
                                            <TextField
                                                required
                                                fullWidth
                                                value={password}
                                                error={!!passwordError}
                                                helperText={passwordError}
                                                onFocus={handlePasswordFocus}
                                                onBlur={handlePasswordBlur}
                                                onChange={(e) => handlePasswordChange(e.target.value)}
                                                type={showPassword ? 'text' : 'password'}
                                                label='Password'
                                                autoComplete='new-password'
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    ),
                                                }}
                                            />
                                            {password && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Password strength:
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color={`${passwordStrength.color}.main`}
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {passwordStrength.feedback}
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(passwordStrength.score / 5) * 100}
                                                        color={passwordStrength.color}
                                                        sx={{
                                                            height: 4,
                                                            borderRadius: 2,
                                                            bgcolor: 'action.hover'
                                                        }}
                                                    />
                                                    {password && (
                                                        <Box sx={{ mt: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                Password requirements:
                                                            </Typography>
                                                            <Stack spacing={0.5}>
                                                                {passwordRequirements.map((requirement, index) => (
                                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {requirement.met ? (
                                                                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                                        ) : (
                                                                            <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                                                        )}
                                                                        <Typography
                                                                            variant="caption"
                                                                            color={requirement.met ? 'success.main' : 'text.secondary'}
                                                                            sx={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {requirement.label}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                        <TextField
                                            required
                                            fullWidth
                                            value={confirmPassword}
                                            error={!!confirmError}
                                            helperText={confirmError}
                                            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                                            onFocus={handleConfirmPasswordFocus}
                                            onBlur={handleConfirmPasswordBlur}
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            label='Confirm Password'
                                            autoComplete='new-password'
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                ),
                                            }}
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel id='favorite-team-label'>Favorite Team *</InputLabel>
                                            <Select
                                                labelId='favorite-team-label'
                                                label='Favorite Team *'
                                                required
                                                onFocus={handleFavoriteTeamFocus}
                                                onBlur={handleFavoriteTeamBlur}
                                                value={favoriteTeamID || ''}
                                                onChange={(e) => setFavoriteTeamID(e.target.value as number)}
                                            >
                                                {
                                                    [...teams].sort((a, b) => {
                                                        if (a.name < b.name) return -1
                                                        if (a.name > b.name) return 1
                                                        return 0
                                                    }).map(team => (
                                                        <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                                                    ))
                                                }
                                            </Select>
                                            <FormHelperText error={!!favoriteTeamError}>{favoriteTeamError}</FormHelperText>
                                        </FormControl>
                                        <Button
                                            disabled={submitDisabled}
                                            type='submit'
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            onClick={handleSubmitClick}
                                        >
                                            {processing ? 'Confirming Account...' : 'Confirm Account'}
                                        </Button>
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Already have an account?{' '}
                                                <RouterLink to='/auth/sign-in' style={{ textDecoration: 'none' }}>
                                                    <Link component='span' sx={{ fontWeight: 600 }}>Sign in</Link>
                                                </RouterLink>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Paper>
            </Container>
        </Box>
    )
}